// The same knowledge as changeMeanings.ts, for a count of rows instead of one row: "2 data u mbyllën
// për rezervime" where the plain noun and verb would say "2 datë rezervueshmërie u krijuan" and leave
// the reader to guess what creating one does. Both the noun and the verb change with the count, so
// each sentence is written in both forms.
type Counted = (n: number) => string;
type Sentences = Partial<Record<string, Counted>>;

const counted = (one: string, many: string): Counted => (n) => `${n} ${n === 1 ? one : many}`;

// Creating says one thing, deleting it (either way) says the other, restoring says the first again.
const onOff = (created: Counted, deleted: Counted): Sentences => ({
  Created: created,
  SoftDeleted: deleted,
  HardDeleted: deleted,
  Restored: created
});

const PICTURES = onOff(
  counted("foto u shtua", "foto u shtuan"),
  counted("foto u hoq", "foto u hoqën")
);

const gtfsBlock = (one: string, many: string): Sentences =>
  onOff(
    counted(`${one} nuk dërgohet më në GTFS`, `${many} nuk dërgohen më në GTFS`),
    counted(`${one} dërgohet sërish në GTFS`, `${many} dërgohen sërish në GTFS`)
  );

const VARIANT_SENTENCES: Record<string, Record<string, Sentences>> = {
  CustomTripWorkingDay: {
    ExceptionDate: {
      ...onOff(
        counted("datë u ndal", "data u ndalën"),
        counted("datë u rikthye në punë", "data u rikthyen në punë")
      ),
      Updated: counted("datë e ndaluar u ndryshua", "data të ndaluara u ndryshuan")
    },
    SelectedWorkingDate: {
      ...onOff(
        counted("datë shtesë u shtua", "data shtesë u shtuan"),
        counted("datë shtesë u hoq", "data shtesë u hoqën")
      ),
      Updated: counted("datë shtesë u ndryshua", "data shtesë u ndryshuan")
    }
  },
  GTFSBlockedEntity: {
    Operator: gtfsBlock("operator", "operatorë"),
    Line: gtfsBlock("linjë", "linja"),
    Trip: gtfsBlock("orar", "orare")
  }
};

const ACTION_SENTENCES: Record<string, Sentences> = {
  CustomTripReservabilityDate: onOff(
    counted("datë u mbyll për rezervime", "data u mbyllën për rezervime"),
    counted("datë u hap për rezervime", "data u hapën për rezervime")
  ),
  BusTripMapping: onOff(
    counted("autobus iu caktua orarit", "autobusë iu caktuan orarit"),
    counted("autobus u hoq nga orari", "autobusë u hoqën nga orari")
  ),
  PermissionRecordUserRoleMapping: onOff(
    counted("leje iu dha rolit", "leje iu dhanë rolit"),
    counted("leje iu hoq rolit", "leje iu hoqën rolit")
  ),
  UserRoleMapping: onOff(
    counted("rol iu dha përdoruesit", "role iu dhanë përdoruesit"),
    counted("rol iu hoq përdoruesit", "role iu hoqën përdoruesit")
  ),
  InvitedUserRoleMapping: onOff(
    counted("rol u caktua në ftesë", "role u caktuan në ftesë"),
    counted("rol u hoq nga ftesa", "role u hoqën nga ftesa")
  ),
  BlockedUser: onOff(
    counted("përdorues u bllokua", "përdorues u bllokuan"),
    counted("përdorues u zhbllokua", "përdorues u zhbllokuan")
  ),
  TravelBundleLocationMapping: onOff(
    counted("qytet u shtua në paketë", "qytete u shtuan në paketë"),
    counted("qytet u hoq nga paketa", "qytete u hoqën nga paketa")
  ),
  TravelBundleIncludeMapping: onOff(
    counted("përfshirje u shtua në paketë", "përfshirje u shtuan në paketë"),
    counted("përfshirje u hoq nga paketa", "përfshirje u hoqën nga paketa")
  ),
  TravelBundlePictureMapping: PICTURES,
  BlogPictureMapping: PICTURES,
  EntityPictureMapping: PICTURES
};

// The whole sentence for a count of rows, when the entity has one; null to fall back to noun and verb.
export const countedSentence = (
  entityName: string,
  variant: string | null,
  action: string,
  count: number
): string | null => {
  const byVariant = variant ? VARIANT_SENTENCES[entityName]?.[variant]?.[action] : undefined;
  const sentence = byVariant ?? ACTION_SENTENCES[entityName]?.[action];
  return sentence ? sentence(count) : null;
};
